<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

  const props = withDefaults(
    defineProps<{
      durationMs: number;
      onDone?: () => void;
      /** When true, freezes the remaining auto-close timer and progress bar. */
      paused?: boolean;
    }>(),
    {
      onDone: undefined,
      paused: false,
    },
  );

  const progress = ref(0);
  const progressPct = computed(() => Math.round(progress.value * 100));
  const shouldRun = computed(() => props.durationMs > 0);

  let rafId: number | undefined;
  let timeoutId: number | undefined;
  let startedAt = 0;
  let remainingMs = 0;

  function stopTimers() {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = undefined;
    }
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  }

  function tick(now: number) {
    const elapsed = now - startedAt;
    const spent = props.durationMs - remainingMs + elapsed;
    progress.value = Math.min(1, spent / props.durationMs);

    if (progress.value < 1) {
      rafId = requestAnimationFrame(tick);
    }
  }

  function run(forMs: number) {
    stopTimers();
    if (!shouldRun.value || forMs <= 0) {
      progress.value = forMs <= 0 ? 1 : 0;
      return;
    }

    remainingMs = forMs;
    startedAt = performance.now();
    rafId = requestAnimationFrame(tick);

    if (props.onDone) {
      timeoutId = window.setTimeout(() => {
        props.onDone?.();
      }, forMs);
    }
  }

  function pause() {
    if (!rafId && !timeoutId) {
      return;
    }

    const elapsed = performance.now() - startedAt;
    remainingMs = Math.max(0, remainingMs - elapsed);
    stopTimers();
  }

  function resume() {
    if (rafId || timeoutId || remainingMs <= 0) {
      return;
    }
    run(remainingMs);
  }

  function reset() {
    stopTimers();
    progress.value = 0;
    remainingMs = props.durationMs;
    if (shouldRun.value && !props.paused) {
      run(remainingMs);
    }
  }

  onMounted(() => {
    remainingMs = props.durationMs;
    if (!props.paused) {
      run(remainingMs);
    }
  });

  onBeforeUnmount(() => stopTimers());

  watch(
    () => props.durationMs,
    () => reset(),
  );

  watch(
    () => props.paused,
    (isPaused) => {
      if (isPaused) {
        pause();
      } else {
        resume();
      }
    },
  );
</script>

<template>
  <div
    v-if="shouldRun"
    class="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden bg-current/15"
  >
    <div
      class="h-full bg-current transition-[width] duration-100 ease-linear"
      :style="{ width: `${progressPct}%` }"
    />
  </div>
</template>
